import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Button, Dialog, Input } from '../../../components/ui';
import ProfileClonePreviewPanel from './ProfileClonePreviewPanel.jsx';
import useProfileClonePreview from '../hooks/useProfileClonePreview.js';

export default function ProfileCloneDialog({ open, profile, onClose, onSuccess }) {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    catalog,
    grants,
    resourceTree,
    businessFunctionsSection,
    hasPermissionsContent,
    canSubmitPermissions,
    isLoading: isPermissionsLoading,
    isDirty,
    error: permissionsError,
    getSubmitGrants,
    updateFeatureGrant,
    updateObjectGrant,
    updateNodeGrants,
    patchGrants,
  } = useProfileClonePreview(profile?.id, open);

  useEffect(() => {
    if (open) {
      setNewName('');
      setError('');
    }
  }, [open, profile?.id]);

  const handleSubmit = async () => {
    if (!newName.trim()) {
      setError('Name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        clone_from_profile_id: profile?.id,
        new_organisation_id: profile?.organisation?.organisation_id,
        new_name: newName.trim(),
      };

      if (canSubmitPermissions) {
        payload.permissions = getSubmitGrants();
      }

      const result = await onSuccess?.(payload);
      if (result?.ok) {
        onClose?.();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEditorDisabled = isSubmitting || isPermissionsLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Clone Profile"
      maxWidth="lg"
      fullWidth
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isSubmitting}
            disabled={isPermissionsLoading}
            onClick={handleSubmit}
          >
            Clone Profile
          </Button>
        </>
      }
    >
      {profile ? (
        <>
          <Typography sx={{ mb: 2 }}>
            You are about to clone profile <strong>{profile.name}</strong> within organisation{' '}
            <strong>{profile.organisation?.name}</strong>.
          </Typography>
          <Input
            label="New profile name"
            required
            value={newName}
            onChange={(event) => {
              setNewName(event.target.value);
              if (error) setError('');
            }}
            error={Boolean(error)}
            errorMessage={error}
            fullWidth
          />
          <ProfileClonePreviewPanel
            catalog={catalog}
            grants={grants}
            resourceTree={resourceTree}
            businessFunctionsSection={businessFunctionsSection}
            hasPermissionsContent={hasPermissionsContent}
            isLoading={isPermissionsLoading}
            error={permissionsError}
            isDirty={isDirty}
            disabled={isEditorDisabled}
            onNodeGrantChange={updateNodeGrants}
            onFeatureChange={updateFeatureGrant}
            onObjectChange={updateObjectGrant}
            onGrantsPatch={patchGrants}
          />
        </>
      ) : null}
    </Dialog>
  );
}
